/** Industry fields the Explore / Opportunities pickers need to hide paused rows and collapse duplicates. */
export type PickerIndustry = {
  index: string;
  title: string;
  assetCount90d: number | null;
  dataUpdated: Date | string | null;
};

function normalizePickerTitle(title: string): string {
  return title.toLowerCase().replace(/[\s_-]+/g, '');
}

/**
 * Case, whitespace, separator, and apostrophe fold for industry labels.
 * `Skin Care`, `skin_care`, and `skin-care` become the same token.
 */
export function canonicalizeIndustryInput(value: string): string {
  return normalizePickerTitle(value).replace(/['\u2019]/g, '');
}

function isLivePickerIndustry(row: PickerIndustry): boolean {
  return row.dataUpdated != null && row.assetCount90d !== 0;
}

function dataUpdatedTime(value: Date | string | null): number {
  if (value == null) return 0;
  const ms = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(ms) ? ms : 0;
}

function isBetterPickerRow(candidate: PickerIndustry, current: PickerIndustry): boolean {
  const candidateCount = candidate.assetCount90d ?? 0;
  const currentCount = current.assetCount90d ?? 0;
  if (candidateCount !== currentCount) return candidateCount > currentCount;
  return dataUpdatedTime(candidate.dataUpdated) > dataUpdatedTime(current.dataUpdated);
}

/**
 * Picker-facing industry list: drop paused/no-data rows, then one option per display title.
 * Membership checks and IAM assignment must keep using the unfiltered repo list.
 */
export function selectPickerIndustries<T extends PickerIndustry>(rows: T[]): T[] {
  const winners = new Map<string, T>();
  for (const row of rows) {
    if (!isLivePickerIndustry(row)) continue;
    const key = normalizePickerTitle(row.title);
    const existing = winners.get(key);
    if (!existing || isBetterPickerRow(row, existing)) {
      winners.set(key, row);
    }
  }
  return [...winners.values()].sort((a, b) => a.title.localeCompare(b.title));
}

/**
 * Resolves free-text industry input to a tenant industry row.
 *
 * Prefers the advertised picker list (live rows, collapsed duplicate titles) so a
 * display title or a sibling slug (`weight_loss` vs `weightloss`) maps to the same
 * canonical index the web picker would submit. Falls back to an unfiltered
 * case-insensitive index match so a paused assigned industry still counts as known.
 */
export function resolveTenantIndustry<T extends PickerIndustry>(
  rows: T[],
  input: string,
): T | undefined {
  const trimmed = input.trim();
  if (!trimmed) return undefined;

  const needle = canonicalizeIndustryInput(trimmed);
  const picker = selectPickerIndustries(rows);
  const pickerHit = picker.find(
    (row) =>
      canonicalizeIndustryInput(row.index) === needle ||
      canonicalizeIndustryInput(row.title) === needle,
  );
  if (pickerHit) return pickerHit;

  const indexNeedle = trimmed.toLowerCase();
  const indexHit = rows.find((row) => row.index.trim().toLowerCase() === indexNeedle);
  if (!indexHit) return undefined;

  // A losing duplicate slug (`entaintaiment`) does not normalize to the winner index
  // (`entertainment`). Map it through the advertised title so cache keys stay on the
  // picker winner. Paused assigned rows have no winner and stay as the raw index.
  return (
    picker.find((row) => normalizePickerTitle(row.title) === normalizePickerTitle(indexHit.title)) ??
    indexHit
  );
}

/** How many advertised titles a refusal names before folding the rest into a count. */
export const MAX_LISTED_UNKNOWN_INDUSTRY_TITLES = 20;

const UNKNOWN_INDUSTRY_PREFIX = "That industry is not one of this organisation's industries.";

const UNKNOWN_INDUSTRY_FALLBACK =
  `${UNKNOWN_INDUSTRY_PREFIX} Pick one from the myIndustry or competitorsIndustry options of ` +
  'nexus_list_filter_options.';

/**
 * Refusal copy for an unmatched industry. Names the advertised picker titles so a
 * model cannot treat the error as licence to guess a different category.
 */
export function unknownIndustryMessage(rows: PickerIndustry[]): string {
  const titles = selectPickerIndustries(rows).map((row) => row.title);
  if (titles.length === 0) return UNKNOWN_INDUSTRY_FALLBACK;

  const shown = titles.slice(0, MAX_LISTED_UNKNOWN_INDUSTRY_TITLES);
  const extra = titles.length > shown.length ? ` (${titles.length} total)` : '';
  return `${UNKNOWN_INDUSTRY_PREFIX} Choose one of: ${shown.join(', ')}${extra}.`;
}
